import { barSegments, formatMoney } from '../../lib/fosApi';

/** Progress toward a goal: confirmed money (solid), reported-but-unconfirmed (light), outstanding pledges (hatched), and the goal line. */
/**
 * With a goal, the whole bar IS the goal: a full bar means the goal is met and the goal line sits at the same place on every bar, so
 * bars for different districts compare directly. Anything beyond the goal is cut off at the end and marked with ». Without a goal the
 * bar shows the composition of what exists (raised, awaiting confirmation, pledged).
 */
export function ProgressBar({ raised, pending, outstanding, goal, tall = false }: { raised: number; pending: number; outstanding: number; goal: number | null; tall?: boolean }) {
  const w = barSegments({ raised, pending, outstanding, goal });
  const pct = (n: number) => `${n}%`;
  const label = `${formatMoney(raised)} raised${goal ? ` of ${formatMoney(goal)} goal` : ''}${pending ? `, ${formatMoney(pending)} awaiting confirmation` : ''}${outstanding ? `, ${formatMoney(outstanding)} pledged and not yet received` : ''}`;
  return (
    <div className={`fos-progress${tall ? ' tall' : ''}`} role="img" aria-label={label}>
      <span className="seg raised" style={{ width: pct(w.raised) }} />
      <span className="seg pending" style={{ width: pct(w.pending) }} />
      <span className="seg outstanding" style={{ width: pct(w.outstanding) }} />
      {goal ? <span className="goal-line" style={{ left: '100%' }} title={`Goal ${formatMoney(goal)}`} /> : null}
      {w.over ? <span className="over-mark" aria-hidden title="More than the goal, counting pledges">»</span> : null}
    </div>
  );
}

export function Legend({ pending = true, outstanding = true }: { pending?: boolean; outstanding?: boolean }) {
  return (
    <div className="fos-legend">
      <span><i className="dot raised" />Raised (confirmed)</span>
      {pending && <span><i className="dot pending" />Reported, awaiting confirmation</span>}
      {outstanding && <span><i className="dot outstanding" />Pledged, not yet received</span>}
    </div>
  );
}

export function Kpi({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: 'good' | 'gold' | 'muted' }) {
  return (
    <div className={`fos-kpi${tone ? ` ${tone}` : ''}`}>
      <span className="fos-kpi-label">{label}</span>
      <strong>{value}</strong>
      {hint ? <small>{hint}</small> : null}
    </div>
  );
}
